// Shared dues status logic usable by both Nuxt app and Cloud Functions.
// No Vue imports; pure TypeScript utilities.

import { toDate } from '../utils/dates.js'

export enum DuesStatusType {
  PAID = 'PAID',
  UNPAID = 'UNPAID',
  EXEMPT_LIFETIME = 'EXEMPT_LIFETIME',
  EXEMPT_REDSHIRT = 'EXEMPT_REDSHIRT',
  EXEMPT_BAND_CAPTAIN = 'EXEMPT_BAND_CAPTAIN',
  EXEMPT_BAND_MEMBER = 'EXEMPT_BAND_MEMBER',
  EXEMPT_SPONSORED = 'EXEMPT_SPONSORED',
  EXEMPT_PENDING_APPROVAL = 'EXEMPT_PENDING_APPROVAL',
}

export interface PurchaseItem {
  orderId: string
  purchasedAt: Date | string
  /** Pool redemptions have no `purchasedAt`, only these (server/api/pool/redeem.post.ts). */
  paidAt?: Date | string
  redeemedAt?: Date | string
  ticketType: string
  accessCode: string
  status: string
  eventbriteOrderId: string
}

export interface ChildPurchaseLike {
  id?: string
  ticketType?: string
  purchasedAt?: Date | string
}

export interface YearlyPurchases {
  membership?: PurchaseItem[]
  vip?: PurchaseItem[]
  parking?: PurchaseItem[]
  children?: ChildPurchaseLike[]
  orderIds?: string[]
}

export interface SubKreweLike {
  id?: string
  music?: string | null
  sponsored?: boolean
  approved?: string | boolean | null
}

export interface MemberLike {
  id?: string
  member_type?: 'member' | 'redshirt' | 'captain' | 'pending'
  lifetime?: boolean
  purchases?: Record<string, YearlyPurchases>
  subkrewe?: SubKreweLike | null
}

export interface DuesStatus {
  shouldPay: boolean
  statusType: DuesStatusType
  displayText: string
  tagValue: string
  tagSeverity: 'success' | 'info' | 'warning' | 'danger' | 'secondary'
  paidAt?: Date | string | null
}

export interface GetMemberDuesStatusOpts {
  paradeYear: string | number
  musicBandValue?: string // default 'band'
}

export function getMemberDuesStatusCore(member: MemberLike | null | undefined, opts: GetMemberDuesStatusOpts): DuesStatus {
  const bandValue = opts.musicBandValue || 'band'
  const paradeYear = String(opts.paradeYear)

  if (!member) {
    return baseUnpaid()
  }

  // Paid?
  const yearlyPurchases = member.purchases?.[paradeYear]
  if (yearlyPurchases?.membership && Array.isArray(yearlyPurchases.membership) && yearlyPurchases.membership.length > 0) {
    const completed = yearlyPurchases.membership.filter(p => (p.status || '').toLowerCase() === 'completed')
    if (completed.length > 0) {
      const latest = completed.sort((a, b) => purchaseMillis(b) - purchaseMillis(a))[0]
      return {
        shouldPay: false,
        statusType: DuesStatusType.PAID,
        displayText: 'Member has paid dues for the upcoming parade',
        tagValue: 'Paid',
        tagSeverity: 'success',
        paidAt: latest ? (latest.purchasedAt || latest.paidAt || latest.redeemedAt || null) : null,
      }
    }
  }

  // Lifetime
  if (member.lifetime === true) {
    return exempt(DuesStatusType.EXEMPT_LIFETIME, 'Member is exempt from annual dues (Lifetime member)', 'Lifetime')
  }

  // Redshirt
  if (member.member_type === 'redshirt') {
    return exempt(DuesStatusType.EXEMPT_REDSHIRT, 'Member is exempt from annual dues (Red Shirt member)', 'Red Shirt')
  }

  const sk = member.subkrewe
  if (sk) {
    // A band waives dues only once it's approved: anyone can register a
    // SubKrewe as a band, and its captain and members were exempt while it
    // was still pending. (A pending band's captain is exempt below, as any
    // pending captain is.)
    const isApprovedBand = sk.music === bandValue && sk.approved === 'Yes'
    // Band captain
    if (member.member_type === 'captain' && isApprovedBand) {
      return exempt(DuesStatusType.EXEMPT_BAND_CAPTAIN, 'Member is exempt from annual dues (Band Captain)', 'Band Captain')
    }
    // Band member
    if (isApprovedBand) {
      return exempt(DuesStatusType.EXEMPT_BAND_MEMBER, 'Member is exempt from annual dues (Band Member)', 'Band Member')
    }
    // Sponsored
    if (sk.sponsored === true) {
      return exempt(DuesStatusType.EXEMPT_SPONSORED, 'Member is exempt from annual dues (Sponsored SubKrewe)', 'Sponsored')
    }
    // Pending approval (captain only)
    if (member.member_type === 'captain' && sk.approved !== 'Yes') {
      return {
        shouldPay: false,
        statusType: DuesStatusType.EXEMPT_PENDING_APPROVAL,
        displayText: 'Member is temporarily exempt from annual dues (Pending SubKrewe approval)',
        tagValue: 'Pending Approval',
        tagSeverity: 'warning',
      }
    }
  }

  return baseUnpaid()
}

/** The purchase times this reads; at runtime they are usually Firestore Timestamps. */
export interface PurchaseTimes {
  purchasedAt?: unknown
  paidAt?: unknown
  redeemedAt?: unknown
}

/**
 * When a purchase was paid, from whatever shape it was stored in.
 * `new Date(timestamp)` is Invalid Date, so sorting on it gave NaN; and a pool
 * redemption has no `purchasedAt`, only `paidAt` / `redeemedAt`.
 */
export function purchaseDate(p: PurchaseTimes): Date | null {
  return toDate(p.purchasedAt) ?? toDate(p.paidAt) ?? toDate(p.redeemedAt)
}

/** `purchaseDate` in ms for sorting, 0 when it isn't known. */
export function purchaseMillis(p: PurchaseTimes): number {
  return purchaseDate(p)?.getTime() ?? 0
}

// Convenience boolean helper mirroring legacy API
export function memberShouldPayDuesCore(member: MemberLike | null | undefined, opts: GetMemberDuesStatusOpts): boolean {
  return getMemberDuesStatusCore(member, opts).shouldPay
}

function baseUnpaid(): DuesStatus {
  return {
    shouldPay: true,
    statusType: DuesStatusType.UNPAID,
    displayText: 'Member has not yet paid dues for the upcoming parade',
    tagValue: 'Unpaid',
    tagSeverity: 'danger',
  }
}

function exempt(type: DuesStatusType, displayText: string, label: string): DuesStatus {
  return {
    shouldPay: false,
    statusType: type,
    displayText,
    tagValue: label,
    tagSeverity: 'info',
  }
}

/**
 * Mapping from DuesStatusType to display label (tagValue)
 * Used for filter dropdowns and UI display
 */
export const DUES_STATUS_LABELS: Record<DuesStatusType, string> = {
  [DuesStatusType.PAID]: 'Paid',
  [DuesStatusType.UNPAID]: 'Unpaid',
  [DuesStatusType.EXEMPT_LIFETIME]: 'Lifetime',
  [DuesStatusType.EXEMPT_REDSHIRT]: 'Red Shirt',
  [DuesStatusType.EXEMPT_BAND_CAPTAIN]: 'Band Captain',
  [DuesStatusType.EXEMPT_BAND_MEMBER]: 'Band Member',
  [DuesStatusType.EXEMPT_SPONSORED]: 'Sponsored',
  [DuesStatusType.EXEMPT_PENDING_APPROVAL]: 'Pending Approval',
}

/**
 * All dues status labels as an array (for filter dropdowns)
 */
export const DUES_STATUS_OPTIONS: string[] = Object.values(DUES_STATUS_LABELS)
